import { CRS } from 'leaflet'
import { WMSTileLayer } from "react-leaflet"
import { memo, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useUserContext } from '../../../../hooks/UserSettingProvider'
import { GeographyLayer, GROUPS } from '../../../../constants/string'
import LayerOverlay from '../../../map/layers/overlays/LayerOverlay'


const PROVINCES = {
  'กระบี่': ['ind_81_z47'],
  'กาญจนบุรี': ['ind_71_z47'],
  'กาฬสินธุ์': ['ind_46_z48'],
  'กำแพงเพชร': ['ind_62_z47'],
  'ขอนแก่น': ['ind_40_z47', 'ind_40_z48'],
  'จันทบุรี': ['ind_22_z47', 'ind_22_z48'],
  'ฉะเชิงเทรา': ['ind_24_z47'],
  'ชลบุรี': ['ind_20_z47'],
  'ชัยนาท': ['ind_18_z47'],
  'ชัยภูมิ': ['ind_36_z47', 'ind_36_z48'],
  'ชุมพร': ['ind_86_z47'],
  'ตรัง': ['ind_92_z47'],
  'ตราด': ['ind_23_z48'],
  'ตาก': ['ind_63_z47'],
  'นครปฐม': ['ind_73_z47'],
  'นครพนม': ['ind_48_z48'],
  'นครราชสีมา': ['ind_30_z47', 'ind_30_z48'],
  'นครศรีธรรมราช': ['ind_80_z47'],
  'นครสวรรค์': ['ind_60_z47'],
  'นราธิวาส': ['ind_96_z47', 'ind_96_z48'],
  'น่าน': ['ind_55_z47'],
  'บึงกาฬ': ['ind_38_z48'],
  'บุรีรัมย์': ['ind_31_z48'],
  'ประจวบคีรีขันธ์': ['ind_77_z47'],
  'ปราจีนบุรี': ['ind_25_z47', 'ind_25_z48'],
  'ปัตตานี': ['ind_94_z47'],
  'พะเยา': ['ind_56_z47'],
  'พังงา': ['ind_82_z47'],
  'พัทลุง': ['ind_93_z47'],
  'พิจิตร': ['ind_66_z47'],
  'พิษณุโลก': ['ind_65_z47'],
  'ภูเก็ต': ['ind_83_z47'],
  'มหาสารคาม': ['ind_44_z48'],
  'มุกดาหาร': ['ind_49_z48'],
  'ยะลา': ['ind_95_z47'],
  'ยโสธร': ['ind_35_z48'],
  'ระนอง': ['ind_85_z47'],
  'ระยอง': ['ind_21_z47'],
  'ราชบุรี': ['ind_70_z47'],
  'ร้อยเอ็ด': ['ind_45_z48'],
  'ลพบุรี': ['ind_15_z47'],
  'ลำปาง': ['ind_52_z47'],
  'ลำพูน': ['ind_51_z47'],
  'ศรีสะเกษ': ['ind_33_z48'],
  'สกลนคร': ['ind_47_z48'],
  'สงขลา': ['ind_90_z47'],
  'สตูล': ['ind_91_z47'],
  'สระบุรี': ['ind_19_z47'],
  'สระแก้ว': ['ind_27_z47', 'ind_27_z48'],
  'สุพรรณบุรี': ['ind_72_z47'],
  'สุราษฎร์ธานี': ['ind_84_z47'],
  'สุรินทร์': ['ind_32_z48'],
  'สุโขทัย': ['ind_64_z47'],
  'หนองคาย': ['ind_43_z48'],
  'หนองบัวลำภู': ['ind_39_z47', 'ind_39_z48'],
  'อำนาจเจริญ': ['ind_37_z48'],
  'อุดรธานี': ['ind_41_z48'],
  'อุตรดิตถ์': ['ind_53_z47'],
  'อุทัยธานี': ['ind_61_z47'],
  'อุบลราชธานี': ['ind_34_z48'],
  'เชียงราย': ['ind_57_z47'],
  'เชียงใหม่': ['ind_50_z47'],
  'เพชรบุรี': ['ind_76_z47'],
  'เพชรบูรณ์': ['ind_67_z47'],
  'เลย': ['ind_42_z47', 'ind_42_z48'],
  'แพร่': ['ind_54_z47'],
  'แม่ฮ่องสอน': ['ind_58_z47'],
  // ปริมณฑล
  'กรุงเทพมหานคร': ['digital_update_zone47', 'digital_update_zone48'],
  'นครนายก': ['digital_update_zone47', 'digital_update_zone48'],
  'นนทบุรี': ['digital_update_zone47', 'digital_update_zone48'],
  'ปทุมธานี': ['digital_update_zone47', 'digital_update_zone48'],
  'พระนครศรีอยุธยา': ['digital_update_zone47', 'digital_update_zone48'],
  'สมุทรปราการ': ['digital_update_zone47', 'digital_update_zone48'],
  'สมุทรสงคราม': ['digital_update_zone47', 'digital_update_zone48'],
  'สมุทรสาคร': ['digital_update_zone47', 'digital_update_zone48'],
  'สิงห์บุรี': ['digital_update_zone47', 'digital_update_zone48'],
  'อ่างทอง': ['digital_update_zone47', 'digital_update_zone48'],
}


// provided by SONGSUK
const AlroLayerOverlay = ({ province }) => {
  const { t } = useTranslation()
  const { user } = useUserContext()
  const layers = useMemo(() => (PROVINCES[province?.name_th] ?? []).join(','), [province?.name_th])
  const canViewMap = useMemo(() => user?.is_superuser || (user?.groups ?? []).includes(GROUPS.DOL), [user])

  const Layer = useCallback(() => {
    return (
      <WMSTileLayer
        url='https://songsuk.alro.go.th:8443/geoserver/alromaps_ind/wms'
        layers={layers}
        minZoom={12}
        minNativeZoom={14}
        maxZoom={20}
        zIndex={350}
        version='1.3.0'
        crs={CRS.EPSG4326}
        styles=''
        format='image/png'
        transparent={true}
      />
    )
  }, [layers])
  
  if (!(canViewMap && layers)) return <></>
  return (
    <LayerOverlay
      name='alro'
      title={<span style={{ color: GeographyLayer.Colors.ALRO }}>{t(GeographyLayer.Labels.ALRO)}</span>}
    >
      <Layer/>
    </LayerOverlay>
  )
}

export default memo(AlroLayerOverlay)